
import { defineComponent, PropType } from "vue";
import { useDateTime } from "@/core/hooks/use-date-time-hook";
import { useRouterCustom } from "@/core/hooks/use-router-custom";
import { IHopDongResponseBase } from "@/core/interfaces/ApiResponses";
import { usePagination } from "@/core/hooks/use-pagination";
import useHopDong from "@/core/hooks/use-hop-dong";
import { withListPage } from "@/core/helpers/list-page-context";
import { formatAmount } from "@/core/helpers/number-format";
import { swalErrNotification, swalNotification } from "@/core/helpers/utils";
import Setting from "@/components/shared/setting/Setting.vue";
import type { SettingAction } from "@/components/shared/setting/Setting.vue";
import IconEye from "@/icons/IconEye.vue";
import IconPencil from "@/icons/IconPencil.vue";
import IconPrint from "@/icons/IconPrint.vue";
import IconTrash from "@/icons/IconTrash.vue";
import { mapMutations } from "vuex";
import { useLoading } from "@/core/hooks/use-loading";
import { HopDongService } from "@/core/services/HopDong.service";
import { openHopDongPdf } from "@/components/shared/pdf-viewer/open-hop-dong-pdf";

export default defineComponent({
  name: "hop-dong-table",

  components: { Setting },

  props: {
    curPage: {
      type: Number,
      default: 0,
    },
    items: {
      type: Array as PropType<Array<IHopDongResponseBase>>,
    },
    isLoading: {
      type: Boolean,
      default: false,
    },
    perPage: {
      type: Number,
      default: 0,
    },
    showEditButton: {
      type: Boolean,
      default: false,
    },
    /** When set (update page), highlight the row for the item being edited */
    selectedId: {
      type: Number,
      default: 0,
    },
  },

  setup() {
    const { moment } = useDateTime();
    const { push } = useRouterCustom();
    const { indexCal } = usePagination();
    const { loaiHopDong } = useHopDong();
    const { startLoading, endLoading } = useLoading();

    return {
      indexCal,
      loaiHopDong,
      moment,
      push,
      formatAmount,
      startLoading,
      endLoading,
    };
  },

  methods: {
    ...mapMutations("ConfirmationDialogModule", {
      setDialog: "setDialog",
      closeDialog: "closeDialog",
    }),
    rowClassName({ row }: { row: IHopDongResponseBase }): string {
      if (this.selectedId > 0 && row.id === this.selectedId) {
        return "current-row";
      }

      return "";
    },
    onDeleteButtonClick(row: IHopDongResponseBase) {
      this.setDialog({
        message: `Bạn có chắc chắn muốn xoá hợp đồng số ${row.so_hop_dong}?`,
        onConfirm: async () => {
          this.closeDialog();
          await this.startLoading();

          try {
            await HopDongService.delete(row.id);
            await this.endLoading();
            await swalNotification("Xoá hợp đồng thành công");

            this.$router.replace({
              name: "danh-sach-hop-dong",
            });
          } catch (e) {
            await this.endLoading();
            await swalErrNotification(e, "Không thể xoá hợp đồng này");
          }
        },
      });
    },
    getSettingActions(row: IHopDongResponseBase): SettingAction[] {
      return [
        {
          icon: IconEye,
          label: "Chi tiết hợp đồng",
          tooltip: "Chi tiết hợp đồng",
          onClick: () => this.push(`/quan-ly/hop-dong/chi-tiet/${row.id}`),
        },
        {
          icon: IconPrint,
          label: "In chi tiết hợp đồng",
          tooltip: "In chi tiết hợp đồng",
          onClick: () => openHopDongPdf(this.$router, row.id),
        },
        {
          icon: IconPencil,
          label: "Chỉnh sửa hợp đồng",
          tooltip: "Chỉnh sửa hợp đồng",
          any: ["data:hopdong:sua"],
          onClick: () =>
            this.push(
              withListPage(`/quan-ly/hop-dong/cap-nhat/${row.id}`, this.curPage)
            ),
        },
        {
          icon: IconTrash,
          label: "Xoá hợp đồng",
          any: ["data:hopdong:xoa"],
          onClick: () => this.onDeleteButtonClick(row),
        },
      ];
    },
  },
});
